import { useEffect, useRef, useCallback } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface UseTurnstileReturn {
  isLoaded: boolean;
  renderWidget: (containerId: string, callback?: (token: string) => void) => string | null;
  resetWidget: (widgetId: string) => void;
  removeWidget: (widgetId: string) => void;
  getSiteKey: () => string | undefined;
}

const SCRIPT_ID = 'cf-turnstile-script';
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function useTurnstile(): UseTurnstileReturn {
  const isLoadedRef = useRef(false);
  const loadPromiseRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    // Log warning if site key is missing
    if (!SITE_KEY) {
      console.warn('[Turnstile] VITE_TURNSTILE_SITE_KEY is not set. Turnstile widget will not render.');
      return;
    }

    // Check if script already exists
    const existingScript = document.getElementById(SCRIPT_ID);
    if (existingScript) {
      isLoadedRef.current = true;
      return;
    }

    // Load Turnstile script
    if (!loadPromiseRef.current) {
      loadPromiseRef.current = new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
        script.async = true;
        script.defer = true;

        window.onTurnstileLoad = () => {
          isLoadedRef.current = true;
          resolve();
        };

        document.head.appendChild(script);
      });
    }
  }, []);

  const getSiteKey = useCallback(() => SITE_KEY, []);

  const renderWidget = useCallback((
    containerId: string,
    callback?: (token: string) => void
  ): string | null => {
    if (!SITE_KEY) {
      console.warn('[Turnstile] Cannot render widget: site key not configured');
      return null;
    }

    const container = document.getElementById(containerId);
    if (!container) {
      console.error(`[Turnstile] Container element '${containerId}' not found`);
      return null;
    }

    if (!window.turnstile) {
      // Wait for script to load and retry
      const checkInterval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkInterval);
          try {
            const widgetId = window.turnstile.render(container, {
              sitekey: SITE_KEY,
              callback,
              theme: 'dark',
              size: 'compact',
            });
            return widgetId;
          } catch (err) {
            console.error('[Turnstile] Render error:', err);
          }
        }
      }, 100);

      // Timeout after 5 seconds
      setTimeout(() => clearInterval(checkInterval), 5000);
      return null;
    }

    try {
      // Clear any existing widget in the container
      container.innerHTML = '';

      const widgetId = window.turnstile.render(container, {
        sitekey: SITE_KEY,
        callback,
        theme: 'dark',
        size: 'compact',
      });
      return widgetId;
    } catch (err) {
      console.error('[Turnstile] Render error:', err);
      return null;
    }
  }, []);

  const resetWidget = useCallback((widgetId: string) => {
    if (window.turnstile && widgetId) {
      try {
        window.turnstile.reset(widgetId);
      } catch (err) {
        console.error('[Turnstile] Reset error:', err);
      }
    }
  }, []);

  const removeWidget = useCallback((widgetId: string) => {
    if (window.turnstile && widgetId) {
      try {
        window.turnstile.remove(widgetId);
      } catch (err) {
        console.error('[Turnstile] Remove error:', err);
      }
    }
  }, []);

  return {
    isLoaded: isLoadedRef.current,
    renderWidget,
    resetWidget,
    removeWidget,
    getSiteKey,
  };
}

/**
 * Get the Turnstile response token from a container
 */
export function getTurnstileToken(containerId: string): string | null {
  const container = document.getElementById(containerId);
  if (!container) return null;

  const input = container.querySelector('input[name="cf-turnstile-response"]');
  return input?.value || null;
}
